<template>
  <div>
    <MatchTagSummary
      :entries="tagEntries"
      :selected-number="selectedNumber"
      @select="selectMatch"
    />

    <div class="font-secondary">
      <template
        v-for="(line, index) in lines"
        :key="`${line.timestamp}-${index}`"
      >
        <div
          v-if="!line.invisible"
          class="text-[14px] leading-[20px] flex relative transition-opacity"
          :class="{
            'opacity-30':
              selectedNumber !== null && line.number !== selectedNumber,
          }"
        >
          <div class="min-w-[90px] text-gray-500 tabular-nums">
            {{ line.timestamp }}
          </div>

          <div class="relative flex-1">
            <MatchTag
              v-if="
                line.tag &&
                (selectedNumber === null || line.tag.number === selectedNumber)
              "
              :ref="(el) => setMarkerRef(line.tag!.number, el)"
              :id="line.tag.number"
              :level="line.tag.level"
              clickable
              class="absolute -top-2 -left-2 z-10"
              @mouseenter="hoveredNumber = line.tag!.number"
              @mouseleave="hoveredNumber = null"
              @click="selectMatch(line.tag.number)"
            />

            <span
              :ref="
                (el) =>
                  line.number != null && line.tag
                    ? setSpanRef(line.number, el)
                    : undefined
              "
              :class="lineClasses(line)"
              v-html="sanitizeHtml(line.text)"
              @mouseenter="line.number != null && (hoveredNumber = line.number)"
              @mouseleave="hoveredNumber = null"
              @click.stop="line.number != null && selectMatch(line.number)"
            ></span>
          </div>
        </div>
      </template>
    </div>

    <Teleport to="body">
      <div
        v-if="selectedMatch && tooltipStyle"
        :style="tooltipStyle"
        class="pointer-events-auto"
        @click.stop
      >
        <MatchTooltip :match="selectedMatch" @view="onView" />
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import MatchTagSummary from './MatchTagSummary.vue'
import MatchTag from './MatchTag.vue'
import MatchTooltip from '../MatchTooltip.vue'
import { ScoreLevel, type Marker } from '@/shared/graphql/router/graphql'
import type { TimelineMatch } from '../timeline-types'
import { sanitizeHtml } from '@/shared/utils/sanitize'
import axios from 'axios'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  watchEffect,
} from 'vue'
import type { ComponentPublicInstance } from 'vue'

export interface TranscriptMatchInput {
  id: string
  level?: ScoreLevel | null
  location?: string | null
  markers?: Array<Marker | null> | null
  transcriptUrl?: string | null
  transcript: Array<{
    timestamp: string
    text: string
    highlight: boolean
  } | null>
}

const props = defineProps<{
  matches: TranscriptMatchInput[]
  timelineMatchById?: ReadonlyMap<string, TimelineMatch>
}>()

const emit = defineEmits<{
  view: [matchId: string]
}>()

interface DisplayLine {
  timestamp: string
  text: string
  highlight: boolean
  invisible: boolean
  level?: ScoreLevel | null
  number?: number
  tag?: { number: number; level?: ScoreLevel | null }
}

const baseLines = ref<{ timestamp: string; text: string }[]>([])
const selectedNumber = ref<number | null>(null)
const hoveredNumber = ref<number | null>(null)

const transcriptUrl = computed(
  () => props.matches.find((m) => m.transcriptUrl)?.transcriptUrl ?? null
)

const tagEntries = computed(() =>
  props.matches.map((match, index) => ({
    number: index + 1,
    level: match.level,
  }))
)

watchEffect(async (onCleanup) => {
  const url = transcriptUrl.value
  if (!url) {
    baseLines.value = []
    return
  }

  let cancelled = false
  onCleanup(() => {
    cancelled = true
  })

  try {
    const { data } = await axios.get(url)
    if (cancelled) return
    baseLines.value = data.lines ?? []
  } catch {
    if (!cancelled) baseLines.value = []
  }
})

const highlightMap = computed(() => {
  const map = new Map<
    string,
    {
      level?: ScoreLevel | null
      number?: number
      tag?: { number: number; level?: ScoreLevel | null }
    }
  >()

  props.matches.forEach((match, index) => {
    const number = index + 1
    let firstHighlightAssigned = false

    for (const line of match.transcript) {
      if (!line || !line.highlight) continue

      const existing = map.get(line.timestamp) ?? {}
      existing.level = match.level
      existing.number = number

      if (!firstHighlightAssigned) {
        existing.tag = { number, level: match.level }
        firstHighlightAssigned = true
      }

      map.set(line.timestamp, existing)
    }
  })

  return map
})

const lines = computed<DisplayLine[]>(() => {
  if (!baseLines.value.length) return []

  let currentGroup: DisplayLine | null = null

  return baseLines.value.reduce((acc, base) => {
    const highlight = highlightMap.value.get(base.timestamp)
    const item: DisplayLine = {
      timestamp: base.timestamp,
      text: base.text,
      highlight: !!highlight,
      invisible: false,
      level: highlight?.level,
      number: highlight?.number,
      tag: highlight?.tag,
    }

    if (item.highlight && currentGroup) {
      item.invisible = true
      currentGroup.text += `<br />${item.text}`
      if (!currentGroup.tag && item.tag) currentGroup.tag = item.tag
    }

    if (item.highlight && !currentGroup) {
      currentGroup = item
    }

    if (!item.highlight) {
      currentGroup = null
    }

    acc.push(item)
    return acc
  }, [] as DisplayLine[])
})

const lineClasses = (line: DisplayLine) => {
  if (!line.highlight || !line.number) return ''

  const isSelected = line.number === selectedNumber.value
  const isHovered = hoveredNumber.value === line.number
  const blockClasses =
    'inline border outline-2 whitespace-pre-wrap rounded-[3px] cursor-pointer'

  if (selectedNumber.value !== null && !isSelected) return ''

  if (isSelected) {
    return {
      [blockClasses]: true,
      'border-[#f5dbda] outline-red-500 bg-[#f5dbda]':
        line.level === ScoreLevel.High,
      'border-[#f5ebdc] outline-yellow-500 bg-[#f5ebdc]':
        line.level === ScoreLevel.Medium,
      'border-[#dceff3] outline-green-500 bg-[#dceff3]':
        line.level === ScoreLevel.Low,
      'border-[#e2e3e5] outline-gray-500 bg-[#e2e3e5]': !line.level,
    }
  }

  if (isHovered) {
    return {
      [blockClasses]: true,
      'border-gray-500/25 outline-gray-500/25 bg-gray-500/25': !line.level,
      'border-[#f0d0d0] outline-[#f0d0d0] bg-[#f0d0d0]':
        line.level === ScoreLevel.High,
      'border-yellow-500/25 outline-yellow-500/25 bg-yellow-500/25':
        line.level === ScoreLevel.Medium,
      'border-green-500/25 outline-green-500/25 bg-green-500/25':
        line.level === ScoreLevel.Low,
    }
  }

  return {
    [blockClasses]: true,
    'border-gray-500/10 outline-gray-500/10 bg-gray-500/10': !line.level,
    'border-[#f9ecec] outline-[#f9ecec] bg-[#f9ecec]':
      line.level === ScoreLevel.High,
    'border-yellow-500/10 outline-yellow-500/10 bg-yellow-500/10':
      line.level === ScoreLevel.Medium,
    'border-green-500/10 outline-green-500/10 bg-green-500/10':
      line.level === ScoreLevel.Low,
  }
}

const selectMatch = (number: number) => {
  selectedNumber.value = selectedNumber.value === number ? null : number
}

watch(
  () => props.matches,
  () => {
    selectedNumber.value = null
  }
)

const markerRefs = new Map<number, HTMLElement>()

const setMarkerRef = (
  number: number,
  el: Element | ComponentPublicInstance | null
) => {
  if (!el) {
    markerRefs.delete(number)
    return
  }
  const node = (el as ComponentPublicInstance).$el ?? (el as HTMLElement)
  markerRefs.set(number, node as HTMLElement)
}

const spanRefs = new Map<number, HTMLElement>()

const setSpanRef = (
  number: number,
  el: Element | ComponentPublicInstance | null
) => {
  if (!el) {
    spanRefs.delete(number)
    return
  }
  const node = (el as ComponentPublicInstance).$el ?? (el as HTMLElement)
  spanRefs.set(number, node as HTMLElement)
}

const fallbackMatch = (
  match: TranscriptMatchInput,
  number: number
): TimelineMatch => ({
  id: match.id,
  type: 'Transcript',
  level: match.level ?? ScoreLevel.High,
  title: 'Transcript',
  score: 0,
  number,
  location: match.location ?? '',
  markers: match.markers ?? [],
  registered: { start: 0, end: 0 },
  infringing: { start: 0, end: 0 },
})

const selectedMatch = computed<TimelineMatch | null>(() => {
  const number = selectedNumber.value
  if (number == null) return null
  const input = props.matches[number - 1]
  if (!input) return null
  const fromParent = props.timelineMatchById?.get(input.id)
  return fromParent ? { ...fromParent, number } : fallbackMatch(input, number)
})

const anchorRect = ref<{ x: number; top: number; bottom: number } | null>(null)

const TOOLTIP_W = 190
const TOOLTIP_H = 128
const TOOLTIP_GAP = 10

const updateAnchor = () => {
  const number = selectedNumber.value
  if (number == null) {
    anchorRect.value = null
    return
  }
  const el = spanRefs.get(number) ?? markerRefs.get(number)
  if (!el) {
    anchorRect.value = null
    return
  }
  const rect = el.getBoundingClientRect()
  anchorRect.value = {
    x: rect.left + rect.width / 2,
    top: rect.top,
    bottom: rect.bottom,
  }
}

watch(
  selectedNumber,
  async () => {
    await nextTick()
    updateAnchor()
  },
  { immediate: true }
)

const onScroll = () => updateAnchor()
const onResize = () => updateAnchor()

const onDocumentClick = () => {
  if (selectedNumber.value == null) return
  selectedNumber.value = null
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, true)
  window.addEventListener('resize', onResize)
  document.addEventListener('click', onDocumentClick)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll, true)
  window.removeEventListener('resize', onResize)
  document.removeEventListener('click', onDocumentClick)
})

const tooltipStyle = computed(() => {
  const a = anchorRect.value
  if (!a) return null
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768
  const padding = 8

  let top = a.top - TOOLTIP_H - TOOLTIP_GAP
  if (top < padding) top = a.bottom + TOOLTIP_GAP
  let left = a.x - TOOLTIP_W / 2
  left = Math.max(padding, Math.min(vw - TOOLTIP_W - padding, left))
  top = Math.max(padding, Math.min(vh - TOOLTIP_H - padding, top))

  return {
    position: 'fixed' as const,
    left: `${left}px`,
    top: `${top}px`,
    width: `${TOOLTIP_W}px`,
    height: `${TOOLTIP_H}px`,
    zIndex: 60,
  }
})

const onView = () => {
  const number = selectedNumber.value
  if (number == null) return
  const input = props.matches[number - 1]
  if (input) emit('view', input.id)
}
</script>
